import { ThemeToggleComponent } from '@/shared/components/theme-toggle/theme-toggle.component';
import { DEFAULT_MENU } from '@/core/constants/sidebar-menu';
import type { User } from '@/core/models/user.model';
import { AuthService } from '@/core/services/auth/auth.service';
import { PermissionService } from '@/core/services/permission/permission.service';
import { Avatar } from '@/shared/components/avatar/avatar.component';
import { Breadcrumb } from '@/shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '@/shared/components/breadcrumb/breadcrumb.interface';
import { Dropdown, DropdownItem } from '@/shared/components/dropdown';
import { IconComponent } from '@/shared/components/icon/icon.component';
import { NotificationComponent } from '@/shared/components/notification/notification.component';
import { Sidebar } from '@/shared/components/sidebar/sidebar.component';
import { SidebarGroup, SidebarItem } from '@/shared/components/sidebar/sidebar.interface';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LightboxComponent } from '@/shared/components/lightbox/lightbox.component';
import { LightboxService } from '@/core/services/lightbox/lightbox.service';
import { RouteLoaderComponent } from '../route-loader/route-loader.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-main-layout',
  imports: [
    RouterOutlet,
    Sidebar,
    Avatar,
    Dropdown,
    Breadcrumb,
    RouteLoaderComponent,
    NotificationComponent,
    IconComponent,
    LightboxComponent,
    ThemeToggleComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayout implements OnInit, AfterViewInit, OnDestroy {
  /** Lightbox partagée : ouverte par `appHtmlLightbox` depuis n'importe quelle page. */
  protected readonly lightbox = inject(LightboxService);

  breadcrumbItems: BreadcrumbItem[] = [];

  @ViewChild(Sidebar) private sidebarRef?: Sidebar;

  // Signaux et entrées
  readonly user = input<User | null>(null, { alias: 'currentUser' });
  readonly searchQuery = signal('');
  readonly searchOpen = signal(false);
  // // Groupes du menu (configurable via input)
  readonly groups = input<SidebarGroup[]>(DEFAULT_MENU);

  private readonly permissions = inject(PermissionService);
  readonly version = environment.version

  /** Groups/items filtered by the current user's role. */
  readonly visibleGroups = computed(() => {
    return (this.groups() ?? DEFAULT_MENU)
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          // N'afficher que les items qui déclarent des rôles et pour lesquels l'utilisateur possède
          // au moins un des rôles requis. Si item.roles est vide ou absent, l'item est caché.
          if (item.hidden) return false;
          const roles = item.roles ?? [];
          return roles.length > 0 && this.permissions.hasRole(...roles);
        }),
      }))
      .filter((group) => group.items.length > 0);
  });

  readonly filteredSidebarItems = computed(() => {
    const q = this.searchQuery().trim().toLowerCase();
    if (!q) return this.visibleGroups();

    return this.visibleGroups()
      .map((group) => ({
        ...group,
        // Si le nom du groupe matche → garder tous ses items, sinon filtrer item par item
        items: group.label.toLowerCase().includes(q)
          ? group.items
          : group.items.filter((i) => i.label.toLowerCase().includes(q)),
      }))
      .filter((group) => group.items.length > 0);
  });

  // Router & breadcrumb
  private readonly router = inject(Router);
  private routerSub?: Subscription;

  // Icônes et menu
  readonly menuItems: DropdownItem[] = [
    { label: 'Mon profil', icon: 'user', action: () => this.goToProfile() },
    { separator: true, label: '' },
    { label: 'Déconnexion', icon: 'log-out', action: () => this.logout() },
  ];

  // Injection de l'élément hôte et de la zone Angular
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly ngZone = inject(NgZone);
  private readonly authService = inject(AuthService);

  // Gestionnaires d'événements conservés en tant que propriétés pour pouvoir être supprimés de manière fiable
  private readonly _onDocumentClick = (event: MouseEvent) => {
    if (!this.searchOpen()) return;

    const target = event.target as Element | null;
    if (!target) return;

    // Si le clic est sur un résultat de recherche (role="option"), fermer le menu déroulant
    if (target.closest('[role="option"]')) {
      this.ngZone.run(() => this.closeSearch());
      return;
    }

    // Si le clic est en dehors du composant hôte, fermer le menu déroulant
    if (!this.hostEl.nativeElement.contains(target)) {
      this.ngZone.run(() => this.closeSearch());
    }
  };

  private readonly _onDocumentKeydown = (event: KeyboardEvent) => {
    if (!this.searchOpen()) return;
    if (event.key === 'Escape') {
      this.ngZone.run(() => this.closeSearch());
    }
  };

  ngAfterViewInit(): void {}

  openMobileSidebar(): void {
    this.sidebarRef?.mobileOpen.set(true);
  }

  ngOnInit(): void {
    // Enregistrer les écouteurs globaux en dehors d'Angular pour éviter des cycles de détection de changement inutiles.
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('click', this._onDocumentClick, true);
      document.addEventListener('keydown', this._onDocumentKeydown, true);
    });

    // Breadcrumbs: subscribe to navigation events and update on init
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.ngZone.run(() => this.updateBreadcrumbs(this.router.url));
      });

    // Initial breadcrumbs
    this.updateBreadcrumbs(this.router.url);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._onDocumentClick, true);
    document.removeEventListener('keydown', this._onDocumentKeydown, true);
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  private updateBreadcrumbs(url: string): void {
    const normalize = (s: string) =>
      (s || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

    const current = normalize(url);

    // Find the best (longest) matching href from DEFAULT_MENU
    let bestGroup: SidebarGroup | null = null;
    let bestItem: SidebarItem | null = null;
    let bestLen = -1;

    for (const group of DEFAULT_MENU) {
      for (const item of group.items) {
        const href = normalize(item.href || '');
        if (!href) continue;

        // Exact match or prefix match (so /app/products/123 matches /app/products)
        if (current === href || current.startsWith(href + '/') || current.startsWith(href)) {
          if (href.length > bestLen) {
            bestLen = href.length;
            bestGroup = group;
            bestItem = item;
          }
        }
      }
    }

    const crumbs: BreadcrumbItem[] = [{ label: 'Accueil', link: '/app', icon: 'house' }];
    if (bestGroup) {
      crumbs.push({ label: bestGroup.label });
    }
    if (bestItem) {
      crumbs.push({ label: bestItem.label, link: bestItem.href, icon: bestItem.icon });
    }
    this.breadcrumbItems = crumbs;
  }

  closeSearch(): void {
    this.searchOpen.set(false);
    this.searchQuery.set('');
  }

  // Actions de remplacement
  goToProfile(): void {
    // naviguer vers le profil de l'utilisateur
    this.router.navigate(['/app/parametres/utilisateurs/' + this.authService.currentUser()?.id]);
  }

  goToSettings(): void {
    // naviguer vers les paramètres
  }

  logout(): void {
    this.authService.logout();
  }
}
